import type { MediaState, MediaWarning } from './media.js';
/** Statusy emisji z AniList, znormalizowane do małych liter. */
export const TITLE_STATUSES = [
  'finished',
  'releasing',
  'not_yet_released',
  'cancelled',
  'hiatus',
] as const;

export type TitleStatus = (typeof TITLE_STATUSES)[number];

export const TITLE_FORMATS = ['tv', 'tv_short', 'movie', 'special', 'ova', 'ona', 'music'] as const;
export type TitleFormat = (typeof TITLE_FORMATS)[number];

export interface TitleBrief {
  id: string;
  anilistId: number | null;
  malId: number | null;
  titleRomaji: string;
  titleEnglish: string | null;
  titleNative: string | null;
  format: TitleFormat | null;
  status: TitleStatus | null;
  seasonYear: number | null;
  episodesTotal: number | null;
  coverUrl: string | null;
  /**
   * Baner poziomy, gdy katalog go ma.
   *
   * W `TitleBrief`, nie tylko w `TitleFull`, bo karuzela na stronie głównej
   * pokazuje kilka tytułów naraz i pobieranie dla każdego pełnych metadanych
   * byłoby kilkoma żądaniami po jedno pole.
   */
  bannerUrl: string | null;
  colorHex: string | null;
  averageScore: number | null;
}

export interface TitleFull extends TitleBrief {
  // `titleNative` i `bannerUrl` są już w `TitleBrief` — powtórzenie tylko
  // dublowałoby deklarację tego samego pola.
  description: string | null;
  durationMin: number | null;
  genres: string[];
  tags: string[];
  isAdult: boolean;
  season: string | null;
  nextAiringAt: string | null;
  nextAiringEpisode: number | null;
  /**
   * Zwiastun, jeśli AniList go zna i jeśli da się z niego zbudować adres.
   *
   * `url` składa **serwer**, w jednym miejscu z białą listą serwisów
   * (`trailerUrl()` w `@anisync/metadata`). Klient dostaje gotowy adres i wstawia go
   * w `href`, zamiast sklejać go z `site` i `id` — te dwa pola przychodzą z obcego
   * serwisu, a sklejanie adresu u każdego odbiorcy znaczyłoby tyle miejsc do
   * sprawdzenia, ile jest odbiorców.
   */
  trailer: { site: string; id: string; url: string } | null;
  metadataSyncedAt: string | null;
}

/**
 * Powiązany tytuł na stronie tytułu.
 *
 * Rodzaj zostaje **surowym napisem z AniLista**: dokładają tam wartości, a nowy
 * rodzaj ma najwyżej nie dostać polskiej etykiety, zamiast wywalać całe
 * powiązanie z odpowiedzi.
 */
export interface RelatedTitle {
  relationType: string;
  title: TitleBrief;
}

/** Polskie etykiety rodzajów powiązań. Brak wpisu znaczy „pokaż surowy rodzaj". */
export const RELATION_LABELS_PL: Record<string, string> = {
  sequel: 'Kontynuacja',
  prequel: 'Poprzednia część',
  parent: 'Seria główna',
  side_story: 'Historia poboczna',
  spin_off: 'Spin-off',
  alternative: 'Inna wersja',
  alternative_version: 'Inna wersja',
  summary: 'Podsumowanie',
  full_story: 'Pełna historia',
};

export interface AiringSlot {
  titleId: string;
  episode: number;
  airingAt: string;
  title: TitleBrief;
}

/** Dostępność jednego odcinka w jednym źródle — zasila listę odcinków w UI. */
export interface EpisodeAvailability {
  episodeNumber: number;
  sources: Array<{
    sourceId: string;
    sourceLabel: string;
    /** Język materiału. `en` znaczy „angielskie napisy", nie „polskie". */
    language: 'pl' | 'en';
    /** Źródło zapasowe: pokazywane dopiero, gdy nie ma żadnego polskiego. */
    fallback: boolean;
    providerTitleId: string;
    /** Hosty odtwarzaczy zgłoszone przez źródło. Puste = jeszcze nie sondowane. */
    hosts: string[];
    /** Czy mamy już ten odcinek w cache i można go odtworzyć natychmiast. */
    cachedMediaFileId: string | null;
    lastError: string | null;
    checkedAt: string | null;
  }>;
  /** Postęp obejrzenia zalogowanego użytkownika, gdy istnieje. */
  progress: { positionSec: number; durationSec: number; completed: boolean } | null;
  /**
   * Materiał, który już mamy u siebie — razem ze stanem.
   *
   * Dzięki temu siatka odcinków mówi, co jest pobrane, co się pobiera, a co
   * dopiero trzeba zdobyć. Wcześniej pole `cachedMediaFileId` istniało wewnątrz
   * `sources`, ale **nigdy nie było wypełniane**, więc interfejs nie miał skąd
   * wiedzieć, że odcinek leży już na dysku.
   */
  mediaFileId: string | null;
  mediaState: MediaState | null;
  /**
   * Ostrzeżenia o pobranym materiale (napisy AI, nietypowa długość). Puste, gdy
   * materiału nie ma albo nie jest gotowy — wtedy nie ma czego oceniać.
   */
  mediaWarnings: MediaWarning[];
}

/** Wynik dopasowania etykiety ze strony do tytułu kanonicznego. */
export interface TitleMatch {
  titleId: string;
  confidence: number;
  reason: string;
}

/** Progi dopasowania. Powyżej AUTO akceptujemy bez pytania, między — kolejka przeglądu. */
export const MATCH_AUTO_ACCEPT = 0.9;
export const MATCH_REVIEW_FLOOR = 0.7;
